import React, { useMemo } from 'react';
import { View, Image, StyleSheet, TouchableOpacity } from 'react-native';
import { Text } from '@/components/common/text';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { Event } from '@/types/residents/event';
import { useEventRsvp } from '@/hooks/residents/useEventRsvp';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import {
  getEventBadgeInfo,
  getEventCapacityDisplay,
  getEventDescription,
  EventBadgeStyle,
} from '@/utils/residents/events/eventHelpers';
import { formatEventDate, formatEventTimeRange } from '@/utils/residents/events/date';
import { eventStrings } from '@/constants/residents/strings';
import { RNButton } from '@/components/common/residents/desktop/Button';
import AddToCalendarButton from '@/components/common/residents/desktop/AddToCalendarButton.web';
import { GlassCard } from '../GlassCard';

const CalendarIcon = RESIDENTS_SVG_COMPONENTS.CALENDAR;
const ClockIcon = RESIDENTS_SVG_COMPONENTS.CLOCK;
const LocationIcon = RESIDENTS_SVG_COMPONENTS.LOCATION;
const GroupIcon = RESIDENTS_SVG_COMPONENTS.GROUP;
const CheckIcon = RESIDENTS_SVG_COMPONENTS.CHECK;

interface DesktopEventGlassCardProps {
  event: Event;
}

const DetailRow: React.FC<{ Icon: any; text: string }> = React.memo(({ Icon, text }) => {
  const { colors } = useThemeColors();
  return (
    <View style={styles.detailRow}>
      <Icon width={18} height={18} color={colors.text.subtle} />
      <Text style={[styles.detailText, { color: colors.text.secondary }]} numberOfLines={1}>{text}</Text>
    </View>
  );
});

DetailRow.displayName = 'DetailRow';

export const DesktopEventGlassCard: React.FC<DesktopEventGlassCardProps> = React.memo(({ event }) => {
  const { colors } = useThemeColors();

  const BADGE_BG: Record<EventBadgeStyle, string> = {
    attending: colors.accent.greenBg,
    gradient: colors.accent.blueBg,
    neutral: colors.glass.buttonBg,
    full: colors.glass.buttonBg,
  };

  const BADGE_TEXT_COLOR: Record<EventBadgeStyle, string> = {
    attending: colors.accent.green,
    gradient: PRIMITIVE_COLORS.resioBlueLight,
    neutral: colors.text.primary,
    full: colors.text.muted,
  };
  const imageUrl = event?.photos?.[0]?.photoUrl;
  const dateText = useMemo(() => formatEventDate(event?.startTime), [event?.startTime]);
  const timeRange = useMemo(
    () => formatEventTimeRange(event?.startTimeLocal || event?.startTime, event?.endTimeLocal || event?.endTime),
    [event?.startTimeLocal, event?.endTimeLocal, event?.startTime, event?.endTime],
  );

  const { accept, decline, isUpdating } = useEventRsvp(event, {
    acceptSuccessMessage: eventStrings?.rsvp?.success || 'Attendance confirmed',
    declineSuccessMessage: 'Attendance updated',
  });

  const badgeInfo = getEventBadgeInfo(event);
  const capacityDisplay = getEventCapacityDisplay(event);
  const description = getEventDescription(event);

  return (
    <GlassCard borderRadius={16} padding={0}>
      <View style={styles.card}>
        {/* Photo Section */}
        <View style={styles.photoSection}>
          <View style={styles.photoContainer}>
            {imageUrl ? (
              <Image source={{ uri: imageUrl }} style={styles.photo} resizeMode="cover" />
            ) : (
              <View style={[styles.photoPlaceholder, { backgroundColor: colors.bg.base }]}>
                <RESIDENTS_SVG_COMPONENTS.PROPERTY_PLACEHOLDER width={220} height={180} />
              </View>
            )}
          </View>
        </View>

        {/* Content Section */}
        <View style={styles.contentSection}>
          <View style={styles.contentRow}>
            <View style={styles.infoCol}>
              {/* Badge */}
              {badgeInfo && (
                <View style={styles.badgesRow}>
                  <View style={[styles.badge, { backgroundColor: BADGE_BG[badgeInfo.style], borderColor: colors.glass.buttonBorder }]}>
                    {badgeInfo.showCheckmark && CheckIcon && (
                      <CheckIcon width={14} height={14} color={BADGE_TEXT_COLOR[badgeInfo.style]} />
                    )}
                    <Text style={[styles.badgeText, { color: BADGE_TEXT_COLOR[badgeInfo.style] }]}>
                      {badgeInfo.text}
                    </Text>
                  </View>
                </View>
              )}

              {/* Title */}
              {!!event?.name && (
                <Text style={[styles.title, { color: colors.text.primary }]} numberOfLines={2}>{event.name}</Text>
              )}

              {/* Details Grid */}
              <View style={styles.detailsGrid}>
                <View style={styles.inlineRow}>
                  <View style={styles.inlineCol}>
                    {dateText ? <DetailRow Icon={CalendarIcon} text={dateText} /> : <View />}
                  </View>
                  <View style={styles.inlineCol}>
                    {event?.location ? <DetailRow Icon={LocationIcon} text={event.location} /> : <View />}
                  </View>
                </View>
                <View style={styles.inlineRow}>
                  <View style={styles.inlineCol}>
                    {timeRange ? <DetailRow Icon={ClockIcon} text={timeRange} /> : <View />}
                  </View>
                  <View style={styles.inlineCol}>
                    <DetailRow Icon={GroupIcon} text={capacityDisplay} />
                  </View>
                </View>
              </View>

              {/* Description */}
              {description.length > 0 && (
                <Text style={[styles.description, { color: colors.text.subtle }]} numberOfLines={3}>{description}</Text>
              )}
            </View>

            {/* Actions Column */}
            <View style={styles.actionsCol}>
              {!event?.rsvp ? (
                <RNButton
                  title={eventStrings.buttons.willAttend}
                  onPress={accept}
                  variant="tertiary"
                  size="md"
                  disabled={isUpdating}
                />
              ) : (
                <RNButton
                  title={eventStrings.buttons.cantAttend}
                  onPress={decline}
                  variant="link"
                  hover="row"
                  size="md"
                  disabled={isUpdating}
                  style={styles.declineButton}
                  textStyle={styles.declineButtonText}
                />
              )}
              <AddToCalendarButton calendarEvent={event} />
            </View>
          </View>
        </View>
      </View>
    </GlassCard>
  );
});

DesktopEventGlassCard.displayName = 'DesktopEventGlassCard';

const styles = StyleSheet.create({
  card: {
    flexDirection: 'row',
  },
  photoSection: {
    padding: 8,
  },
  photoContainer: {
    width: 220,
    borderRadius: 12,
    overflow: 'hidden',
  },
  photo: {
    width: '100%',
    height: 180,
  },
  photoPlaceholder: {
    width: 220,
    height: 180,
    alignItems: 'center',
    justifyContent: 'center',
  },
  contentSection: {
    flex: 1,
    padding: 16,
  },
  contentRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 16,
  },
  infoCol: {
    flex: 1,
    minWidth: 0,
    gap: 6,
  },
  actionsCol: {
    width: 180,
    alignItems: 'flex-end',
    gap: 8,
  },
  badgesRow: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
    paddingTop: 2,
  },
  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 999,
    borderWidth: 1,
  },
  badgeText: {
    ...REDESIGN_TYPOGRAPHY.captionMedium,
  },
  title: {
    ...REDESIGN_TYPOGRAPHY.cardTitleLarge,
    fontSize: 18,
    lineHeight: 24,
  },
  detailsGrid: {
    marginTop: 4,
    rowGap: 6,
  },
  inlineRow: {
    flexDirection: 'row',
    gap: 16,
    alignItems: 'center',
  },
  inlineCol: {
    flex: 1,
  },
  detailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  detailText: {
    ...REDESIGN_TYPOGRAPHY.body,
    flex: 1,
  },
  description: {
    ...REDESIGN_TYPOGRAPHY.small,
    lineHeight: 18,
    marginTop: 4,
  },
  declineButton: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: PRIMITIVE_COLORS.resioBlue,
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 16,
  },
  declineButtonText: {
    color: PRIMITIVE_COLORS.resioBlue,
    fontWeight: '700',
  },
});
